import React from 'react';

import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

import Button from './Button';
import Modal, { ModalBody, ModalFooter } from './Modal';
import Typography from './Typography';

import type { LucideIcon } from 'lucide-react';

export interface ConfirmBoxProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: string;
	description: string;
	confirmText?: string;
	cancelText?: string;
	variant?: 'primary' | 'danger' | 'warning' | 'info';
	isLoading?: boolean;
	icon?: LucideIcon;
}

const ConfirmBox: React.FC<ConfirmBoxProps> = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	variant = 'primary',
	isLoading = false,
	icon: CustomIcon,
}) => {
	const variantConfig = {
		primary: {
			buttonVariant: 'primary' as const,
			icon: Info,
			iconColor: 'text-primary-600',
			iconBg: 'bg-primary-100',
		},
		danger: {
			buttonVariant: 'danger' as const,
			icon: AlertCircle,
			iconColor: 'text-error-600',
			iconBg: 'bg-error-100',
		},
		warning: {
			buttonVariant: 'warning' as const,
			icon: AlertTriangle,
			iconColor: 'text-warning-600',
			iconBg: 'bg-warning-100',
		},
		info: {
			buttonVariant: 'primary' as const,
			icon: Info,
			iconColor: 'text-blue-600',
			iconBg: 'bg-blue-100',
		},
	};

	const config = variantConfig[variant];
	const Icon = CustomIcon || config.icon;

	return (
		<Modal isOpen={isOpen} onClose={onClose} showCloseButton={false} size="sm">
			<ModalBody className="pt-8">
				<div className="flex flex-col items-center text-center">
					<div className={`p-3 rounded-full ${config.iconBg} mb-4`}>
						<Icon className={`w-8 h-8 ${config.iconColor}`} />
					</div>
					<Typography variant="h4" className="mb-2 font-semibold">
						{title}
					</Typography>
					<Typography variant="body2" color="muted" className="mb-2">
						{description}
					</Typography>
				</div>
			</ModalBody>
			<ModalFooter className="border-t-0 pt-2 pb-8 flex flex-col sm:flex-row space-x-0 sm:space-x-3 space-y-3 sm:space-y-0 px-8">
				<Button
					variant="outline"
					onClick={onClose}
					disabled={isLoading}
					fullWidth
					className="sm:flex-1"
				>
					{cancelText}
				</Button>
				<Button
					variant={config.buttonVariant}
					onClick={onConfirm}
					loading={isLoading}
					fullWidth
					className="sm:flex-1"
				>
					{confirmText}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default ConfirmBox;
